import { FC } from 'react';
import { Divider, Stack } from '@mui/material';
import {
  FilterButton,
  Filters,
  OpenSearchFilter,
  SelectFilter,
} from 'components/filters';
import { ROUTES } from 'config';
import {
  Link,
  ListTotal,
  Table,
  TableToolbar,
  TableWrapper,
  Typography,
  ActionsMenu,
} from 'melp-design/components';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import {
  adminSuppliersFiltersSchema,
  useAdminSuppliersStatistics,
} from 'store/admin-suppliers';
import { COLOR_BY_STATUS, supplierStatuses } from 'store/suppliers';
import { useSearchParams } from 'utils/navigation';
import { useModalContext } from 'store/modal';
import { AdminCreateSupplierModal } from 'containers/admin';

export const AdminSuppliersLit: FC = () => {
  const { t } = useTranslation();
  const { openModal } = useModalContext();

  const { searchParams: filters, navigate } = useSearchParams(
    adminSuppliersFiltersSchema,
  );

  const { data: suppliers, isLoading } = useAdminSuppliersStatistics(filters);

  return (
    <TableWrapper>
      <TableToolbar>
        <Stack gap={1}>
          <Stack
            direction="row"
            justifyContent="flex-end"
            alignItems="center"
            gap={1}
          >
            <ActionsMenu
              type="spread"
              items={[
                {
                  label: t('marketplaceSuppliers.addSupplier'),
                  color: 'primary',
                  onClick: () => {
                    openModal({
                      component: AdminCreateSupplierModal,
                      size: 'lg',
                    });
                  },
                },
              ]}
            />
          </Stack>

          <Divider />

          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            gap="10px"
          >
            <ListTotal total={suppliers?.total} />
            <Stack direction="row" alignItems="center" gap="10px">
              <Filters
                value={filters}
                initialValues={adminSuppliersFiltersSchema.parse({})}
                onChange={(v) => navigate({ params: { ...v, page: 1 } })}
              >
                {({ value, initialValues, setFilter }) => (
                  <>
                    <FilterButton
                      label={t('common.status')}
                      value={value.status}
                      initialValue={initialValues.status}
                      onChange={(v) => setFilter('status', v)}
                    >
                      {({ value, applyFilter, clearFilter, close }) => (
                        <SelectFilter
                          name="status"
                          value={{ value }}
                          onApplyFilter={({ value }) => applyFilter(value)}
                          onClearFilter={clearFilter}
                          onCancel={close}
                          searchable={false}
                          options={supplierStatuses.map((status) => ({
                            key: status,
                            label: t(`status.${status}`),
                          }))}
                          multiple={false}
                        />
                      )}
                    </FilterButton>

                    <OpenSearchFilter
                      value={value.name}
                      onApplyFilter={(v) => setFilter('name', v)}
                      placeholder={t('common.search')}
                    />
                  </>
                )}
              </Filters>
            </Stack>
          </Stack>
        </Stack>
      </TableToolbar>

      <Table
        data={suppliers?.items ?? []}
        isLoading={isLoading}
        columns={[
          {
            id: 'name',
            header: t('marketplaceSuppliers.name'),
            cell: ({ row: { original: supplier } }) => (
              <Link
                label={[
                  supplier.name,
                  supplier.isParentCompany ? `(${t('common.private')})` : null,
                ]
                  .filter(Boolean)
                  .join(' ')}
                to={ROUTES.admin.suppliers.details.replace(':id', supplier.id)}
              />
            ),
          },
          {
            id: 'createdAt',
            header: t('marketplaceSuppliers.creationDate'),
            cell: ({ row: { original: supplier } }) =>
              moment(supplier.createdAt).format('l'),
          },
          {
            id: 'items',
            header: t('marketplaceSuppliers.activeItems'),
            cell: ({ row: { original: supplier } }) => supplier.items,
          },
          {
            id: 'orders',
            header: t('marketplaceSuppliers.completedOrders'),
            cell: ({ row: { original: supplier } }) => supplier.orders,
          },
          {
            id: 'status',
            header: t('common.status'),
            cell: ({ row: { original: supplier } }) => (
              <Typography color={COLOR_BY_STATUS[supplier.status]}>
                {t(`status.${supplier.status}`)}
              </Typography>
            ),
          },
        ]}
      />
    </TableWrapper>
  );
};
